import React from "react";
import { ChevronLeft } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { IMAGES } from "@/shared/lib/images";
import { SyncStatusIndicator, OfflineBanner } from "@/components/global/SyncStatusIndicator";
import type { SyncStatus } from "@/components/global/SyncStatusIndicator";
import { HelpButton } from "@/features/help/HelpButton";
import { NotificationButton } from "@/features/help/NotificationButton";

export interface TopBarProps {
  title: string;
  showBack?: boolean;
  onBack?: () => void;
  rightAction?: React.ReactNode;
  /** Node rendered horizontally centred in the bar (e.g. Home's centred Help). */
  centerAction?: React.ReactNode;
  /** Show the shared Help (?) button in the right cluster. Default true. */
  showHelp?: boolean;
  /** Show the shared Notification (bell) button in the right cluster. Default true. */
  showNotification?: boolean;
  showLogo?: boolean;
  className?: string;
  syncStatus?: SyncStatus;
  pendingCount?: number;
}

export const TopBar: React.FC<TopBarProps> = ({
  title,
  showBack = false,
  onBack,
  rightAction,
  centerAction,
  showHelp = true,
  showNotification = true,
  showLogo = false,
  className,
  syncStatus = "synced",
  pendingCount = 0,
}) => {
  return (
    <>
      <header
        className={cn(
          "sticky top-0 z-50 w-full min-h-[56px] gradient-primary shadow-md",
          "flex items-center justify-between px-4 py-3",
          "pt-[max(env(safe-area-inset-top),12px)]",
          className
        )}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {showBack && (
            <button
              onClick={onBack}
              className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/10 active:scale-95 transition-all"
              aria-label="Go back"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
          )}
          {showLogo && (
            <img
              src={IMAGES.logo}
              alt="AGXL Logo"
              width={32}
              height={32}
              loading="eager"
              decoding="async"
              className="w-8 h-8 object-contain"
            />
          )}
          <h1 className="text-xl font-semibold text-white truncate">{title}</h1>
        </div>

        {/* Centre region — horizontally centred in the bar, independent of the
            side clusters (e.g. Home's centred Help). pointer-events kept off the
            wrapper so it never blocks the title; re-enabled on its children. */}
        {centerAction && (
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center pointer-events-none [&>*]:pointer-events-auto">
            {centerAction}
          </div>
        )}

        <div className="flex items-center gap-3 ml-2">
          {/* Voice Help + Notifications — present on authenticated screens unless a
              screen opts out (showHelp / showNotification), left of any per-screen
              action. gap-3 for fat-finger-friendly tap spacing. */}
          {showNotification && <NotificationButton />}
          {showHelp && <HelpButton />}
          {rightAction}
        </div>
      </header>
      {syncStatus === "offline" && <OfflineBanner />}
    </>
  );
};
